import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React from "react";
import * as S from "../VisitMode.styled";
import {
  getLabelOptionText,
  LABEL_OPTIONS,
  sanitizeLabel,
} from "../visitMode.utils";

/**
 * The observations a broker taps rather than types, at a door, one-handed.
 *
 * The list offered is fixed, and anything the broker adds themselves joins it
 * for this candidate only. Remembering a made-up label for the next one means
 * keeping a list per office, which is a place to store it that does not exist
 * yet.
 */
interface IProps {
  value: string[];
  onChange: (next: string[]) => void;
}

const CheckMark = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
    aria-hidden={true}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const VisitLabels = (props: IProps) => {
  const { value, onChange } = props;

  const [isAdding, setIsAdding] = React.useState(false);
  const [draft, setDraft] = React.useState("");

  const options = LABEL_OPTIONS.map(getLabelOptionText);
  // Whatever the broker wrote sits after the offered ones, in the order it was
  // added, so the fixed list stays where the hand expects it.
  const custom = value.filter(label => !options.includes(label));

  const toggle = (label: string) => {
    onChange(
      value.includes(label)
        ? value.filter(current => current !== label)
        : [...value, label],
    );
  };

  const handleAdd = () => {
    const label = sanitizeLabel(draft);

    setDraft("");
    setIsAdding(false);

    if (!label || value.includes(label)) {
      return;
    }

    onChange([...value, label]);
  };

  const renderChip = (label: string) => {
    const isSelected = value.includes(label);

    return (
      <S.LabelChip
        key={label}
        type="button"
        $isSelected={isSelected}
        onClick={() => toggle(label)}
      >
        {isSelected && <CheckMark />}
        {label}
      </S.LabelChip>
    );
  };

  return (
    <S.LabelRow>
      {options.map(renderChip)}
      {custom.map(renderChip)}

      {isAdding ? (
        <S.LabelInput
          autoFocus={true}
          value={draft}
          placeholder={getLocalizedText("visit_mode.labels.add")}
          onChange={event => setDraft(event.target.value)}
          onBlur={handleAdd}
          onKeyDown={event => {
            if (event.key === "Enter") {
              // The field sits inside no form of its own, but the card around
              // it has one, and a bare Enter would submit that rather than add
              // a word.
              event.preventDefault();
              handleAdd();
            }

            if (event.key === "Escape") {
              setDraft("");
              setIsAdding(false);
            }
          }}
        />
      ) : (
        <S.LabelAddChip
          type="button"
          $isSelected={false}
          onClick={() => setIsAdding(true)}
        >
          {`+ ${getLocalizedText("visit_mode.labels.add")}`}
        </S.LabelAddChip>
      )}
    </S.LabelRow>
  );
};
